// Port of Weather/WeatherService.swift: network-first forecasts with cache fallback; cache-only when offline.
// Never strips or rewrites `fetchedAt`.

import type { DataFreshness, EpochMs, GeoCoordinate, WeatherForecast } from '../core/models';
import { forecastFreshness } from '../core/domain';
import { distance } from '../core/geo';
import type { ConnectivityProviding } from '../net/connectivity';
import { WeatherCache } from './weatherCache';
import { weatherErrorFrom, type WeatherError, type WeatherProvider } from './weatherProvider';

/** Where a result's forecast came from. */
export type WeatherSource =
  /** Downloaded during this request. */
  | 'network'
  /** Read from the on-device WeatherCache (possibly old — check `freshness` and `fetchedAt`). */
  | 'cache';

/**
 * Outcome of a forecast request: the best available forecast plus how fresh it is and, when the network could not
 * supply a new one, why. Plain data (the Swift computed properties are filled in by `makeWeatherResult`).
 */
export interface WeatherResult {
  /** The forecast, if any (network or cache). Its `fetchedAt` is the original download time. */
  readonly forecast?: WeatherForecast;
  /** Origin of `forecast`; undefined when there is none. */
  readonly source?: WeatherSource;
  /** Freshness of `forecast` relative to the service clock (FreshnessPolicies.weather). */
  readonly freshness?: DataFreshness;
  /** Why no new forecast could be downloaded (offline, HTTP failure…), even when a cached one is returned. */
  readonly error?: WeatherError;
  /** A forecast exists and is not expired. Unknown freshness is never treated as usable. */
  readonly isUsableForPlanning: boolean;
  /** When the forecast was downloaded (shown to the hiker as "updated …"). */
  readonly fetchedAt?: EpochMs;
}

export function makeWeatherResult(fields: {
  forecast?: WeatherForecast;
  source?: WeatherSource;
  freshness?: DataFreshness;
  error?: WeatherError;
}): WeatherResult {
  const result: {
    forecast?: WeatherForecast;
    source?: WeatherSource;
    freshness?: DataFreshness;
    error?: WeatherError;
    isUsableForPlanning: boolean;
    fetchedAt?: EpochMs;
  } = {
    isUsableForPlanning:
      fields.forecast !== undefined && fields.freshness !== undefined && fields.freshness !== 'expired',
  };
  if (fields.forecast !== undefined) {
    result.forecast = fields.forecast;
    result.fetchedAt = fields.forecast.fetchedAt;
  }
  if (fields.source !== undefined) result.source = fields.source;
  if (fields.freshness !== undefined) result.freshness = fields.freshness;
  if (fields.error !== undefined) result.error = fields.error;
  return result;
}

export interface WeatherServiceOptions {
  provider: WeatherProvider;
  cache: WeatherCache;
  connectivity: ConnectivityProviding;
  now?: () => EpochMs;
}

/**
 * - Online: a forecast still fresh and within `reuseDistanceMeters` is served from the cache (saves battery and data)
 *   unless `forceRefresh`; otherwise the provider is asked, the answer cached and returned. On failure the nearest
 *   cached forecast is returned with the error attached.
 * - Offline: the nearest cached forecast with `error = network(offline)`.
 * Concurrent requests for the same cache key share one provider call.
 */
export class WeatherService {
  /** Maximum distance (m) between a fresh cached forecast and the request for the network to be skipped. */
  static readonly reuseDistanceMeters = 1500;

  private readonly provider: WeatherProvider;
  private readonly cache: WeatherCache;
  private readonly connectivity: ConnectivityProviding;
  private readonly now: () => EpochMs;
  private readonly inFlight = new Map<string, Promise<WeatherForecast>>();

  constructor(options: WeatherServiceOptions) {
    this.provider = options.provider;
    this.cache = options.cache;
    this.connectivity = options.connectivity;
    this.now = options.now ?? Date.now;
  }

  /** Best available forecast for `location` at `elevation` (see the class documentation for the policy). */
  async forecast(location: GeoCoordinate, elevation?: number, forceRefresh = false): Promise<WeatherResult> {
    const cached = await this.cache.forecast(location, elevation);

    if (!this.connectivity.isOnline) {
      return this.resultFromCache(cached, { kind: 'network', error: { kind: 'offline' } });
    }
    if (
      !forceRefresh &&
      cached &&
      forecastFreshness(cached, this.now()) === 'fresh' &&
      distance(cached.location, location) <= WeatherService.reuseDistanceMeters
    ) {
      return this.resultFromCache(cached, undefined);
    }

    try {
      const fresh = await this.fetch(location, elevation);
      return makeWeatherResult({ forecast: fresh, source: 'network', freshness: forecastFreshness(fresh, this.now()) });
    } catch (error) {
      return this.resultFromCache(cached, weatherErrorFrom(error));
    }
  }

  /** Cached forecast only — never touches the network. `error` is `noData` when nothing suitable is cached. */
  async cachedForecast(location: GeoCoordinate, elevation?: number): Promise<WeatherResult> {
    return this.resultFromCache(await this.cache.forecast(location, elevation), undefined);
  }

  private resultFromCache(cached: WeatherForecast | undefined, error: WeatherError | undefined): WeatherResult {
    if (!cached) return makeWeatherResult({ error: error ?? { kind: 'noData' } });
    const freshness = forecastFreshness(cached, this.now());
    return makeWeatherResult({ forecast: cached, source: 'cache', freshness, error });
  }

  /** Downloads and caches a forecast, joining an identical request already in flight. */
  private fetch(location: GeoCoordinate, elevation: number | undefined): Promise<WeatherForecast> {
    const key = WeatherCache.key(location, elevation);
    const running = this.inFlight.get(key);
    if (running) return running;
    const task = (async () => {
      const forecast = await this.provider.forecast(location, elevation);
      try {
        await this.cache.store(forecast);
      } catch (error) {
        // Still return the forecast: failing to cache must not hide fresh weather from the hiker.
        console.error('Could not cache forecast', error);
      }
      return forecast;
    })();
    this.inFlight.set(key, task);
    const clear = () => {
      if (this.inFlight.get(key) === task) this.inFlight.delete(key);
    };
    task.then(clear, clear);
    return task;
  }
}
